/**
 * Figma Node IDs:
 * Design Frame: 1:3734
 * Instance IDs: 1:8020, 1:8023, 1:8026, 1:8029, 1:8032, 1794:3257, 1:8035, 1:8038, 1:8041, 1:8044, 1:8047, 1794:3261, 1:8050, 1:8053, 1:8056, 1:8059, 1:8062, 1794:3265, 1:7775, 1:7778, 1:7781, 1:7784, 1:7787, 1794:3268, 1:7790, 1:7793, 1:7796, 1:7799, 1:7802, 1794:3272, 1:7805, 1:7808, 1:7811, 1:7814, 1:7817, 1794:3276, 1:7974, 1:7977, 1:7980, 1:7983, 1:7986, 1794:3279, 1:7989, 1:7992, 1:7995, 1:7998, 1:8001, 1794:3287, 1:8004, 1:8007, 1:8010, 1:8013, 1:8016, 1794:3295, 337:16294, 337:16298, 337:16302, 337:16307, 337:16311, 1794:3283, 337:16315, 337:16319, 337:16323, 337:16328, 337:16332, 1794:3291, 337:16336, 337:16339, 337:16342, 337:16346, 337:16349, 1794:3298, 1:7913, 1:7917, 1:7921, 1:7925, 1:7929, 1794:3301, 1:7933, 1:7937, 1:7941, 1:7945, 1:7949, 1794:3305, 1:7953, 1:7957, 1:7961, 1:7965, 1:7969, 1794:3309, 1:7821, 1:7824, 1:7827, 1:7830, 1:7833, 1794:3312, 1:7836, 1:7839, 1:7842, 1:7845, 1:7848, 1794:3316, 1:7851, 1:7854, 1:7857, 1:7860, 1:7863, 1794:3320, 3:1946, 3:1949, 3:1952, 3:1955, 3:1958, 1794:3323, 3:1961, 3:1964, 3:1967, 3:1970, 3:1973, 1794:3331, 3:1976, 3:1979, 3:1982, 3:1985, 3:1988, 1794:3339, 1515:81408, 1515:81412, 1515:81416, 1515:81421, 1515:81425, 1794:3327, 1515:81429, 1515:81433, 1515:81437, 1515:81442, 1515:81446, 1794:3335, 1515:81450, 1515:81454, 1515:81458, 1515:81463, 1515:81467, 1794:3343, 912:16931, 912:16933, 912:17003, 1700:23291, 1700:23293, 1700:23295, 912:16927, 912:17023, 912:17025, 1700:23300, 1700:23302, 1700:23304, 912:16929, 912:17039, 912:17041, 1700:23309, 1700:23311, 1700:23313, 912:17011, 1700:23298, 912:16937, 1700:23316, 912:16935, 1700:23307, 1794:18047, 1794:18049, 1794:18051, 1794:18053, 1794:18244, 1794:18246, 1794:18248, 1794:18332, 1794:18334, 1794:18336, 1794:18420, 1794:18422, 1794:18424, 1794:18508, 1794:18510, 1794:18512, 1794:18596, 1794:18598, 1794:18600
 * Composite: true
 * Last Updated: 2024-12-23
 * Generated by: Claude Code figma-component command
 */
import React from "react";
import { useIntl, defineMessages } from "react-intl";
import {
  Loader,
  ChevronRight,
  ChevronLeft,
  type LucideProps,
} from "lucide-react";
import {
  buttonRecipe,
  buttonContentClass,
  mainIconOrderVariants,
  textOrder,
  trailingIconOrderVariants,
  iconSize,
} from "./Button.css";
import { srOnly } from "@labxchange/design_tokens";
import { getDir } from "@learningblocks/shared-components/lib/Translations";
import {
  ButtonType,
  ButtonSize,
  IconPosition,
  ButtonShape,
  ButtonSurface,
  TrailingIconPosition,
} from "./constants";

interface BaseLxButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  shape?: ButtonShape;
  Icon?: React.ComponentType<LucideProps>;
  text?: string;
  onClickHandler?: () => void;
  iconPosition?: IconPosition;
  trailingIcon?: TrailingIconPosition | null;
  loading?: boolean;
  disabled?: boolean;
  /** Custom accessible name for icon-only and icon-with-text buttons. */
  ariaLabel?: string;
  /**
   * Disclosure state for a button that reveals and hides a related region,
   * rendered as `aria-expanded`. Pair with `ariaControls` so the button's state
   * and the region it controls are exposed to assistive technology
   * (WCAG 4.1.2 Name, Role, Value).
   */
  ariaExpanded?: boolean;
  /** Id of the region this button controls, rendered as `aria-controls`. */
  ariaControls?: string;
  testId?: string;
  /** Optional `data-title` attribute, used as a stable automation hook. */
  dataTitle?: string;
}

/** The two variants that only ever render on colour. */
type OnColourOnlyVariant = Extract<ButtonType, "ghost" | "oncolor">;
/** Every variant whose focus ring follows the surface it is placed on. */
type SurfaceAwareVariant = Exclude<ButtonType, OnColourOnlyVariant>;

/**
 * `variant` and `surface`, paired so the invalid combination cannot be written.
 *
 * `ghost` and `oncolor` are permanently fixed to the amber on-colour ring — a
 * settled design decision, not an inconsistency to clean up. They only ever
 * render on colour, so the recipe hard-codes the ring in a compound variant
 * emitted after the surface rule, and it wins. Do not make them surface-aware.
 *
 * The consequence for this type: passing `surface` alongside them would be
 * accepted and silently discarded, and asking for `variant="ghost"
 * surface="light"` would read as a request for the 1.9:1-on-white ring this
 * component exists to keep away from light surfaces. So the first member allows
 * any variant but no `surface`, and only the second — restricted to the
 * surface-aware variants — accepts one.
 *
 * Consequence worth knowing: a caller holding a widened `ButtonType` (e.g.
 * `BackAndForthSlab`'s `nextButtonVariant`) cannot pass `surface` either, since
 * nothing proves at compile time that the value isn't `ghost`. Such callers
 * declare the surface on their container instead, which is the better shape
 * anyway — see `focusRingOuterVar` in Button.css.ts.
 */
type SurfaceProps =
  | { variant?: ButtonType; surface?: never }
  | {
      variant?: SurfaceAwareVariant;
      /**
       * Overrides, for this one button, the surface declared by the container
       * around it. The ring is drawn outside the border box, so WCAG 1.4.11
       * measures it against that surface, not against the button's own fill.
       *
       * Normally you do not pass this: a container that paints a dark panel sets
       * `focusRingOuterVar` in its own style (see `slabContainer` in
       * BackAndForthSlab.css.ts) and every button inside inherits it. Left unset
       * everywhere, the ring is the light-surface blue.
       */
      surface?: ButtonSurface;
    };

export type LxButtonProps = BaseLxButtonProps & SurfaceProps;

const messages = defineMessages({
  loading: {
    id: "lx_button.loading",
    defaultMessage: "Loading",
    description: "Label for button when in loading state",
  },
});

export const LxButton: React.FC<LxButtonProps> = ({
  variant = "primary",
  size = "medium",
  shape = "brick",
  surface,
  Icon,
  text,
  onClickHandler,
  iconPosition = "start",
  trailingIcon = null,
  loading = false,
  disabled = false,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  testId,
  dataTitle,
}) => {
  const intl = useIntl();
  const isRtl = getDir(intl.locale) === "rtl";

  // `ghost`/`oncolor` are deliberately fixed to the amber on-colour ring, so a
  // surface is meaningless on them (see `SurfaceProps`). `LxButtonProps` already
  // makes that a compile error; drop it here as well so a JS caller or a cast
  // cannot smuggle in a declaration the CSS would then ignore.
  const isOnColourOnly = variant === "ghost" || variant === "oncolor";
  const appliedSurface = isOnColourOnly ? undefined : surface;

  const isCircle = shape === "circle";
  const isIconOnly = isCircle || (!text && !!Icon);
  const shouldUseAriaLabel = isIconOnly || !!Icon;
  const showTrailingIcon = !!trailingIcon && !isCircle && !!text;
  const isDisabled = disabled || loading;

  const className = buttonRecipe({
    type: variant,
    surface: appliedSurface,
    shape,
    iconPosition,
    brickSize: shape === "brick" ? size : undefined,
    circleSize: shape === "circle" ? size : undefined,
  });

  const renderTrailingChevron = () => {
    if (!trailingIcon) return null;

    let ChevronIcon: React.ComponentType<LucideProps>;

    if (trailingIcon === "next") {
      ChevronIcon = isRtl ? ChevronLeft : ChevronRight;
    } else {
      ChevronIcon = isRtl ? ChevronRight : ChevronLeft;
    }

    return (
      <ChevronIcon
        className={`${trailingIconOrderVariants[trailingIcon]} ${iconSize}`}
      />
    );
  };

  const iconWithClass = Icon ? (
    <span className={mainIconOrderVariants[iconPosition]}>
      <Icon className={iconSize} />
    </span>
  ) : null;

  return (
    <button
      type="button"
      className={className}
      onClick={isDisabled ? undefined : onClickHandler}
      disabled={isDisabled}
      aria-label={shouldUseAriaLabel ? ariaLabel : undefined}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-busy={loading || undefined}
      data-testid={testId || undefined}
      data-title={dataTitle || undefined}
    >
      <span className={buttonContentClass}>
        {loading ? (
          <>
            <Loader className={iconSize} />
            <span className={srOnly}>
              {intl.formatMessage(messages.loading)}
            </span>
          </>
        ) : (
          <>
            {showTrailingIcon && renderTrailingChevron()}
            {iconWithClass}
            {text && !isIconOnly && (
              <span className={textOrder}>
                {intl.formatMessage({
                  id: text,
                  defaultMessage: text,
                  description: "Button text",
                })}
              </span>
            )}
          </>
        )}
      </span>
    </button>
  );
};

export default LxButton;
